// ─── Collections rule resolution (Task #4830) ────────────────────────────────
// Collections rules live at two levels: account defaults (`collection_settings`)
// and per-property overrides (`collection_property_settings`, where NULL means
// "inherit"). This module owns the merge so the engine and the UI's
// Inherited/Override badges can never disagree — the settings editor previews
// exactly what the runner will do because both call `resolveCollectionSettings`.
//
// Lives in shared/ (not server/) for that reason. Imported by
// server/services/collections-runner.ts and client/src/pages/app-collections/*.

export const COLLECTION_PAYMENT_FREQUENCIES = ["weekly", "bi-weekly", "monthly"] as const;
export type CollectionPaymentFrequency = (typeof COLLECTION_PAYMENT_FREQUENCIES)[number];

export function isCollectionPaymentFrequency(v: unknown): v is CollectionPaymentFrequency {
  return typeof v === "string" && (COLLECTION_PAYMENT_FREQUENCIES as readonly string[]).includes(v);
}

/** The values used when neither a property override nor an account row supplies one. */
export const COLLECTION_ACCOUNT_DEFAULTS = {
  negativeBalanceThreshold: -299,
  paymentPercentage: 10,
  paymentFrequency: "weekly" as CollectionPaymentFrequency,
  paymentDeadlineDays: 7,
  offerLimit: 3,
  // NULL/null = count offers all-time, which is the pre-#4830 behaviour.
  offerResetDays: null as number | null,
  cooldownDays: 7,
} as const;

/** Which level supplied each resolved value. Drives the UI's inherited-vs-custom badges. */
export type CollectionFieldSource = "override" | "account" | "default";

export interface ResolvedCollectionSettings {
  negativeBalanceThreshold: number;
  paymentPercentage: number;
  paymentFrequency: CollectionPaymentFrequency;
  paymentDeadlineDays: number;
  offerLimit: number;
  offerResetDays: number | null;
  cooldownDays: number;
  sources: Record<CollectionRuleField, CollectionFieldSource>;
}

export const COLLECTION_RULE_FIELDS = [
  "negativeBalanceThreshold",
  "paymentPercentage",
  "paymentFrequency",
  "paymentDeadlineDays",
  "offerLimit",
  "offerResetDays",
  "cooldownDays",
] as const;
export type CollectionRuleField = (typeof COLLECTION_RULE_FIELDS)[number];

// Structural input types rather than the Drizzle row types: the client receives
// these as JSON (numerics arrive as strings, timestamps as strings), and the
// server passes real rows. Both satisfy these shapes.
export interface CollectionRuleInput {
  negativeBalanceThreshold?: string | number | null;
  paymentPercentage?: string | number | null;
  paymentFrequency?: string | null;
  paymentDeadlineDays?: number | string | null;
  offerLimit?: number | string | null;
  offerResetDays?: number | string | null;
  cooldownDays?: number | string | null;
}

/** Parse a numeric-as-string (or number) column; null when absent or unparseable. */
function num(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** Parse an integer column; null when absent or unparseable. */
function int(v: string | number | null | undefined): number | null {
  const n = num(v);
  return n === null ? null : Math.trunc(n);
}

/**
 * Merge property overrides over account defaults, field by field:
 * `override ?? account ?? built-in default`.
 *
 * A value that is present but unparseable (e.g. a corrupt numeric string) is
 * treated as absent and falls through to the next level rather than resolving
 * to NaN and silently disabling eligibility for everyone at that property.
 */
export function resolveCollectionSettings(
  account: CollectionRuleInput | null | undefined,
  override: CollectionRuleInput | null | undefined,
): ResolvedCollectionSettings {
  const sources = {} as Record<CollectionRuleField, CollectionFieldSource>;

  function pick<T>(
    field: CollectionRuleField,
    read: (src: CollectionRuleInput) => T | null,
    fallback: T,
  ): T {
    if (override) {
      const v = read(override);
      if (v !== null) {
        sources[field] = "override";
        return v;
      }
    }
    if (account) {
      const v = read(account);
      if (v !== null) {
        sources[field] = "account";
        return v;
      }
    }
    sources[field] = "default";
    return fallback;
  }

  const negativeBalanceThreshold = pick(
    "negativeBalanceThreshold",
    (s) => num(s.negativeBalanceThreshold),
    COLLECTION_ACCOUNT_DEFAULTS.negativeBalanceThreshold,
  );
  const paymentPercentage = pick(
    "paymentPercentage",
    (s) => num(s.paymentPercentage),
    COLLECTION_ACCOUNT_DEFAULTS.paymentPercentage,
  );
  const paymentFrequency = pick<CollectionPaymentFrequency>(
    "paymentFrequency",
    (s) => (isCollectionPaymentFrequency(s.paymentFrequency) ? s.paymentFrequency : null),
    COLLECTION_ACCOUNT_DEFAULTS.paymentFrequency,
  );
  const paymentDeadlineDays = pick(
    "paymentDeadlineDays",
    (s) => int(s.paymentDeadlineDays),
    COLLECTION_ACCOUNT_DEFAULTS.paymentDeadlineDays,
  );
  const offerLimit = pick("offerLimit", (s) => int(s.offerLimit), COLLECTION_ACCOUNT_DEFAULTS.offerLimit);
  const offerResetDays = pick<number | null>(
    "offerResetDays",
    (s) => int(s.offerResetDays),
    COLLECTION_ACCOUNT_DEFAULTS.offerResetDays,
  );
  const cooldownDays = pick("cooldownDays", (s) => int(s.cooldownDays), COLLECTION_ACCOUNT_DEFAULTS.cooldownDays);

  return {
    negativeBalanceThreshold,
    paymentPercentage,
    paymentFrequency,
    paymentDeadlineDays,
    offerLimit,
    offerResetDays,
    cooldownDays,
    sources,
  };
}

// ─── Balance eligibility ─────────────────────────────────────────────────────
// The concepts below are deliberately separated so that the ONE piece we cannot
// yet write — a canonical "amount owed", which requires knowing PadSplit's sign
// convention — is visibly absent rather than silently guessed. See the
// BLOCKED note at the bottom of this section.

/**
 * The raw stored balance, parsed from `members.total_due`.
 *
 * Returns null when the column is absent, empty, or unparseable. Null is NOT
 * folded into 0: "we hold no balance for this member" and "this member's
 * balance is zero" are different facts, and only the latter is evidence of
 * anything. Both are ineligible, but they are logged distinguishably upstream.
 */
export function parseStoredBalance(raw: string | number | null | undefined): number | null {
  return num(raw);
}

/**
 * Is there a balance worth evaluating at all?
 *
 * False for null/unparseable (no data) and for exactly zero (settled). This is
 * the only "zero means settled" assumption in the module, and it holds under
 * either sign convention, so it is safe to assert today.
 */
export function hasCollectableBalance(stored: number | null): stored is number {
  return stored !== null && Number.isFinite(stored) && stored !== 0;
}

/** The size of a stored balance, with its (unknown-meaning) sign discarded. */
export function balanceMagnitude(stored: number): number {
  return Math.abs(stored);
}

/**
 * Is this member's balance far enough gone to warrant a collection notice?
 *
 * Behaviour is preserved EXACTLY from the pre-refactor implementation (which
 * itself was lifted verbatim from server/schedule-cron.ts:281-289). It is
 * re-expressed here in terms of the concepts above so the two distinct regimes
 * hiding inside the old two-line body are visible:
 *
 *   threshold <= 0  — the documented, coherent regime. Reduces exactly to
 *                     `magnitude >= |threshold|`, i.e. sign-agnostic: a row is
 *                     eligible once the SIZE of its balance reaches the size of
 *                     the threshold, whichever convention wrote it. This is why
 *                     the old code needed two branches to say one thing.
 *
 *   threshold > 0   — reachable (the UI and validators allow up to +100) and
 *                     INCOHERENT: the negative branch degenerates to
 *                     `stored <= threshold`, which every negative balance
 *                     satisfies regardless of size, so a +100 threshold makes a
 *                     -$1 balance eligible while a +$99 one is not. Preserved
 *                     verbatim rather than "fixed", because fixing it means
 *                     choosing a sign convention we do not yet have. Tracked as
 *                     a product bug, not a refactor target.
 *
 * Exactly zero, null, and NaN are never eligible under any threshold.
 *
 * BLOCKED — deliberately absent: a `canonicalAmountOwed(stored)` that maps the
 * stored value onto "dollars this member actually owes". Writing it requires
 * PadSplit's confirmed convention (is a credit negative or positive?). Until
 * the warm-browser contract lands, this module treats the sign as UNKNOWN and
 * compares magnitudes only. Do not add that function on a guess.
 */
export function isBalanceEligible(totalDue: number, threshold: number): boolean {
  if (!hasCollectableBalance(Number.isFinite(totalDue) ? totalDue : null)) return false;

  if (threshold <= 0) {
    return balanceMagnitude(totalDue) >= Math.abs(threshold);
  }

  // threshold > 0 — preserved verbatim; see the INCOHERENT note above.
  if (totalDue < 0) return true;
  return totalDue >= threshold;
}

/** The partial payment we ask for, given a balance and the resolved percentage. */
export function partialPaymentAmount(totalDue: number, paymentPercentage: number): number {
  return Math.abs(totalDue) * (paymentPercentage / 100);
}
